import ExportButton from '../../components/ExportButton';
import { useEffect, useState } from "react";
import { Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumbs from "../../components/Breadcrumbs";
import CustomModal from "../../components/CustomModal";
import Notification from "../../components/Notification";
import Stats from "../../components/Stats";
import { fetchFuelOverview } from "../../store/Slices/FuelSlice";
import "../../styles/Fuel.css";
import "../../styles/Services.css";
import { formatDate } from "../../utils/helper";
import { avgCostLiers, avgEfficiency, plusIcon, totalFilledLiters, totalFuelCost } from "../../utils/svgs";
import { FiInfo } from "react-icons/fi";
import AddFuelLog from "./AddFuelLog";
import FuelLists from "./FuelList";
import FuelListDescription from "./FuelListDescription";
import FuelListing from "./FuelListing";
import VehicleDescription from "./VehicleDescription";
import PermissionWrapper from "../../components/PermissionWrapper";
import { AppMainModules, FuelPermission, FuelSubModules } from "../../utils/app-constant";
import { useFuelListingExport } from "./useFuelListingExport";

const Fuel = () => {
    const dispatch = useDispatch();
    const [showAddLogModal, setShowAddLogModal] = useState(false);
    const [activeManagementTab, setActiveManagementTab] = useState("tracking");
    const [listingSearchText, setListingSearchText] = useState("");
    const [listingSearchFilters, setListingSearchFilters] = useState({});
    const {
        hasActiveFilters: hasActiveListingFilters,
        exportLoading,
        pdfExportLoading,
        excelExportLoading,
        handleExportPdf,
        handleExportExcel,
    } = useFuelListingExport(listingSearchText, listingSearchFilters);

    useEffect(() => {
        dispatch(fetchFuelOverview());
    }, [])

    const { activeTab, fetchFuelOverviewData } = useSelector((state) => state.fuel);

    const fuelStatsData = [
        {
            title: (
                <h6 style={{ position: "relative", width: "fit-content" }}>
                    Total Liters Filled
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Data based on {fetchFuelOverviewData?.totalTripCount} Trips</Tooltip>}
                    >
                        <div style={{
                            cursor: "pointer", display: "flex",
                            alignItems: "center", border: "none", padding: "0",
                            position: "absolute", right: "-25px", top: "-10px"
                        }}>
                            <FiInfo size={18} />
                        </div>
                    </OverlayTrigger>
                </h6>
            ),
            value: fetchFuelOverviewData?.totalLiterFilled?.total_liter_filled,
            text: (
                <>
                    <span>Last updated: </span>
                    <b>{formatDate(fetchFuelOverviewData?.totalLiterFilled?.last_updated)}</b>
                </>
            ),
            icon: totalFilledLiters,
        },
        {
            title: (
                <h6 style={{ position: "relative", width: "fit-content" }}>
                    Total Fuel Cost
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Data based on {fetchFuelOverviewData?.totalTripCount} Trips</Tooltip>}
                    >
                        <div style={{
                            cursor: "pointer", display: "flex",
                            alignItems: "center", border: "none", padding: "0",
                            position: "absolute", right: "-25px", top: "-10px"
                        }}>
                            <FiInfo size={18} />
                        </div>
                    </OverlayTrigger>
                </h6>
            ),
            value: fetchFuelOverviewData?.totalFuelCost,
            text: (
                <>
                    <span>Calculated from all refuels</span>
                </>
            ),
            icon: totalFuelCost,
        },
        {
            title: (
                <h6 style={{ position: "relative", width: "fit-content" }}>
                    Average Efficiency
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Data based on {fetchFuelOverviewData?.totalTripCount} Trips</Tooltip>}
                    >
                        <div style={{
                            cursor: "pointer", display: "flex",
                            alignItems: "center", border: "none", padding: "0",
                            position: "absolute", right: "-25px", top: "-10px"
                        }}>
                            <FiInfo size={18} />
                        </div>
                    </OverlayTrigger>
                </h6>
            ),
            value: `${fetchFuelOverviewData?.averageEfficiency} km/L`,
            text: (
                <>
                    <span>Average across all trips</span>
                </>
            ),
            icon: avgEfficiency,
        },
        {
            title: (
                <h6 style={{ position: "relative", width: "fit-content" }}>
                    Average Cost
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Data based on {fetchFuelOverviewData?.totalTripCount} Trips</Tooltip>}
                    >
                        <div style={{
                            cursor: "pointer", display: "flex",
                            alignItems: "center", border: "none", padding: "0",
                            position: "absolute", right: "-25px", top: "-10px"
                        }}>
                            <FiInfo size={18} />
                        </div>
                    </OverlayTrigger>
                </h6>
            ),
            value: `${fetchFuelOverviewData?.averageCostPerLiter} per Litre`,
            text: (
                <>
                    <span>Weighted average fuel price</span>
                </>
            ),
            icon: avgCostLiers,
        },
    ];

    return (
        <div className='trip_page fuel_main'>
            <Breadcrumbs values={["Fuel Management", activeManagementTab === "tracking" ? "Tracking" : "Listing"]} />

            <div className='access_view'>
                <div className="d-flex align-items-center gap-3">
                    <h2>Fuel Management</h2>

                    <div className="access_btn">
                        <button
                            className={activeManagementTab === "tracking" ? "active" : ""}
                            onClick={() => setActiveManagementTab("tracking")}
                        >
                            Tracking
                        </button>
                        <button
                            className={activeManagementTab === "listing" ? "active" : ""}
                            onClick={() => setActiveManagementTab("listing")}
                        >
                            Listing
                        </button>
                    </div>
                </div>

                <div className='services_buttons'>
                    {activeManagementTab === "listing" && (
                        <ExportButton
                            type="pdf"
                            onClick={handleExportPdf}
                            disabled={!hasActiveListingFilters || exportLoading}
                            loading={pdfExportLoading}
                            hasActiveFilters={hasActiveListingFilters}
                        />
                    )}

                    {activeManagementTab === "listing" && (
                        <ExportButton
                            type="excel"
                            onClick={handleExportExcel}
                            disabled={!hasActiveListingFilters || exportLoading}
                            loading={excelExportLoading}
                            hasActiveFilters={hasActiveListingFilters}
                        />
                    )}

                    <Notification />

                    <PermissionWrapper
                        permission={FuelPermission.Create}
                        module={FuelSubModules.Fuel}
                        mainModule={AppMainModules.fuelManagement}
                    >
                        <button onClick={() => setShowAddLogModal(true)}>{plusIcon} Add Fuel Log</button>
                    </PermissionWrapper>
                </div>
            </div>

            {activeManagementTab === "tracking" && (
                <>
                    <Stats statsData={fuelStatsData} />


                    <div className="page_container">
                        <Row className="trip_layout_row">
                            <Col md={3}>
                                <div className="trip_list_scroll">
                                    <FuelLists />
                                </div>
                            </Col>
                            <Col md={9}>
                                <div className="trip_list_scroll">
                                    {
                                        activeTab === "fuel" ? <FuelListDescription /> : <VehicleDescription />
                                    }
                                </div>
                            </Col>
                        </Row>
                    </div>
                </>
            )}

            {activeManagementTab === "listing" && (
                <FuelListing
                    searchText={listingSearchText}
                    setSearchText={setListingSearchText}
                    searchFilters={listingSearchFilters}
                    setSearchFilters={setListingSearchFilters}
                />
            )}

            <CustomModal
                heading="Add Fuel Log"
                show={showAddLogModal}
                handleClose={() => setShowAddLogModal(false)}
            >
                <AddFuelLog handleClose={() => setShowAddLogModal(false)} />
            </CustomModal>
        </div>
    );
};

export default Fuel;
